import { useState, useCallback } from 'react';
import { Sparkles, Loader2, AlertCircle, Copy, CheckCircle, Download } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { useStore } from '../store/useStore';
import { saveAs } from 'file-saver';

const WORKER_URL = import.meta.env.VITE_WORKER_URL || 'http://localhost:8787';

export function DiagnosisPanel() {
  const { macroData, apiKey, apiUrl, apiModel, diagnosisReport, setDiagnosisReport } = useStore();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const requestDiagnosis = useCallback(async () => {
    if (!macroData) {
      setError('请先上传学生数据');
      return;
    }

    if (!apiKey) {
      setError('请先在设置中配置 API Key');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`${WORKER_URL}/api/diagnose`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          macroData,
          apiKey,
          apiUrl: apiUrl || 'https://api.siliconflow.cn/v1',
          apiModel: apiModel || 'deepseek-ai/DeepSeek-V3',
        }),
      });

      if (!response.ok) {
        throw new Error(`请求失败: ${response.status}`);
      }

      const data = await response.json();

      if (data.success && data.report) {
        setDiagnosisReport(data.report);
      } else {
        throw new Error(data.error || '诊断失败');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : '未知错误');
    } finally {
      setIsLoading(false);
    }
  }, [macroData, apiKey, apiUrl, apiModel, setDiagnosisReport]);

  const copyReport = useCallback(() => {
    if (diagnosisReport) {
      navigator.clipboard.writeText(diagnosisReport);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [diagnosisReport]);

  const exportHtml = useCallback(() => {
    if (!macroData || !diagnosisReport) return;

    const redListText = macroData.redList.map((r) => r.question).join('、');
    const blackListText = macroData.blackList.map((b) => b.question).join('、');

    const dimensionScoresHtml = macroData.avgScores
      .map((d) => `<div class="dimension-item"><span>${d.dimension}</span><span>${d.avgScore.toFixed(2)}</span></div>`)
      .join('');

    const weakestGroupHtml = macroData.weakestGroup
      ? `<div class="info-item"><span class="label">薄弱小组：</span><span class="value orange">${macroData.weakestGroup.groupId}</span></div>`
      : '';

    const html = `<!DOCTYPE html>
<html lang="zh-CN">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>班级诊断报告 - ${macroData.className}</title>
  <style>
    body { font-family: "Microsoft YaHei", Arial, sans-serif; max-width: 900px; margin: 40px auto; padding: 20px; background: #f5f5f5; }
    .card { background: white; border-radius: 12px; padding: 30px; margin-bottom: 20px; box-shadow: 0 2px 8px rgba(0,0,0,0.1); }
    h1 { color: #1a73e8; margin-bottom: 10px; }
    .meta { color: #666; font-size: 14px; margin-bottom: 20px; }
    .section { margin: 20px 0; }
    .section h2 { color: #333; font-size: 18px; border-bottom: 2px solid #1a73e8; padding-bottom: 8px; margin-bottom: 15px; }
    .info-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px; }
    .info-item { display: flex; align-items: center; gap: 8px; }
    .label { color: #666; }
    .value { font-weight: 500; }
    .value.green { color: #16a34a; }
    .value.red { color: #dc2626; }
    .value.orange { color: #ea580c; }
    .dimension-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin-top: 15px; }
    .dimension-item { background: #f0f4ff; padding: 15px; border-radius: 8px; text-align: center; }
    .dimension-item span:first-child { display: block; font-size: 12px; color: #666; margin-bottom: 5px; }
    .dimension-item span:last-child { font-size: 20px; font-weight: bold; color: #1a73e8; }
    .report-content { line-height: 1.8; color: #444; white-space: pre-wrap; }
    .export-time { text-align: right; color: #999; font-size: 12px; margin-top: 20px; }
  </style>
</head>
<body>
  <div class="card">
    <h1>${macroData.className} - 班级诊断报告</h1>
    <div class="meta">任务ID: ${macroData.taskId}</div>

    <div class="section">
      <h2>班级概况</h2>
      <div class="info-grid">
        <div class="info-item">
          <span class="label">班级：</span>
          <span class="value">${macroData.className}</span>
        </div>
        <div class="info-item">
          <span class="label">红榜维度：</span>
          <span class="value green">${redListText || '无'}</span>
        </div>
        <div class="info-item">
          <span class="label">黑榜维度：</span>
          <span class="value red">${blackListText || '无'}</span>
        </div>
        ${weakestGroupHtml}
      </div>
    </div>

    <div class="section">
      <h2>维度分析</h2>
      <div class="dimension-grid">
        ${dimensionScoresHtml}
      </div>
    </div>
  </div>

  <div class="card">
    <div class="section">
      <h2>AI 诊断报告</h2>
      <div class="report-content">${diagnosisReport}</div>
    </div>
  </div>

  <div class="export-time">导出时间: ${new Date().toLocaleString()}</div>
</body>
</html>`;

    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    saveAs(blob, `班级诊断报告_${macroData.className}_${new Date().toLocaleDateString()}.html`);
  }, [macroData, diagnosisReport]);

  return (
    <div className="bg-white rounded-xl shadow-lg p-6">
      <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
        <Sparkles className="w-5 h-5 text-yellow-500" />
        AI 教学诊断
      </h3>

      {!macroData ? (
        <div className="text-center py-8 text-gray-500">
          请先上传学生评价数据
        </div>
      ) : (
        <>
          <div className="mb-4 p-4 bg-gray-50 rounded-lg">
            <h4 className="font-medium text-gray-700 mb-2">班级概况</h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-500">班级：</span>
                <span className="font-medium">{macroData.className}</span>
              </div>
              <div>
                <span className="text-gray-500">红榜维度：</span>
                <span className="font-medium text-green-600">
                  {macroData.redList.map((r) => r.question).join('、')}
                </span>
              </div>
              <div>
                <span className="text-gray-500">黑榜维度：</span>
                <span className="font-medium text-red-600">
                  {macroData.blackList.map((b) => b.question).join('、')}
                </span>
              </div>
              {macroData.weakestGroup && (
                <div>
                  <span className="text-gray-500">薄弱小组：</span>
                  <span className="font-medium text-orange-600">
                    {macroData.weakestGroup.groupId}
                  </span>
                </div>
              )}
            </div>
          </div>

          {!diagnosisReport && !isLoading && (
            <button
              onClick={requestDiagnosis}
              disabled={!apiKey}
              className={`w-full py-3 rounded-lg font-medium transition-colors ${
                apiKey
                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              {apiKey ? '生成 AI 诊断报告' : '请先配置 API Key'}
            </button>
          )}

          {isLoading && (
            <div className="flex items-center justify-center py-8 gap-3 text-blue-600">
              <Loader2 className="w-6 h-6 animate-spin" />
              <span>AI 正在分析班级数据...</span>
            </div>
          )}

          {error && (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3 text-red-700">
              <AlertCircle className="w-5 h-5" />
              {error}
            </div>
          )}

          {diagnosisReport && (
            <div className="mt-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-medium text-gray-700">诊断报告</h4>
                <div className="flex items-center gap-2">
                  <button
                    onClick={copyReport}
                    className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
                  >
                    {copied ? <CheckCircle className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                    {copied ? '已复制' : '复制'}
                  </button>
                  <button
                    onClick={exportHtml}
                    className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
                  >
                    <Download className="w-4 h-4" />
                    导出HTML
                  </button>
                </div>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg prose prose-sm max-w-none">
                <ReactMarkdown>{diagnosisReport}</ReactMarkdown>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}